import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, compressImage } from '@structural-climbing/ui'

interface Props {
  gymId: string
  coverUrl: string | null
  onCoverChange: (url: string) => void
}

const ACCEPTED = 'image/png,image/jpeg,image/webp'

export function CoverUpload({ gymId: _gymId, coverUrl, onCoverChange }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setError(null)
    setUploading(true)
    try {
      const compressed = await compressImage(file, 1.5 * 1024 * 1024, 1600)
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', compressed)
      const res = await fetch(`${import.meta.env.VITE_API_URL as string}/gym-cover`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) {
        const { error: msg } = (await res.json()) as { error?: string }
        setError(msg ?? t('common.uploadFailed'))
        return
      }
      const { url } = (await res.json()) as { url: string }
      onCoverChange(url)
    } catch {
      setError(t('common.uploadFailedRetry'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative flex h-36 w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-divider bg-content2"
        onClick={() => inputRef.current?.click()}
      >
        {coverUrl ? (
          <img src={coverUrl} alt="Cover photo" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs text-foreground/40">{t('cover.noCover')}</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void handleFile(file)
            e.target.value = ''
          }}
        />
        <Button
          size="sm"
          variant="flat"
          isLoading={uploading}
          isDisabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {coverUrl ? t('cover.changeCover') : t('cover.uploadCover')}
        </Button>
        <p className="text-xs text-foreground/40">{t('cover.hint')}</p>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  )
}
